import React, { useContext } from 'react'
import { useLocation } from 'react-router-dom'

import { Helmet } from 'react-helmet-async'
import PropTypes from 'prop-types'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams, sortArrayBy } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { generateUrls } from '@hooks/useGeoSuburb'
import useGeoStates from '@hooks/useGeoStates'
import useGeoSuburbs from '@hooks/useGeoSuburbs'
import useSelector from '@hooks/useSelector'

import GeoSuburbListItem from '../GeoSuburbListItem'

const filterGeoStates = (geoCountryCode, geoStates) => {
  if (!geoCountryCode) return []
  return Object.values(geoStates).filter(state => state.country_code === geoCountryCode)
}

const buildPageFilterFields = (entities, state, setState) => {
  const { geoCountries, geoStates } = entities
  const { geoCountryCode } = state

  const filteredGeoStates = filterGeoStates(geoCountryCode, geoStates)

  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      callbacks: { onChange: e => setState({ geoCountryCode: e }) },
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'country_code',
      label: 'Country',
      values: sortArrayBy(Object.values(geoCountries), 'asc', 'title').map(geoCountry => ({
        key: geoCountry.code,
        label: geoCountry.title,
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: geoCountryCode ? 'All' : 'Select Country',
        inputComponent: 'select',
        style,
      },
      dependentKey: 'country_code',
      key: 'geo_state_id',
      label: 'Filter by State',
      values: sortArrayBy(filteredGeoStates, 'asc', 'title').map(geoState => ({
        key: geoState.id,
        label: geoState.title,
      })),
    },
  ]
}

const defaultState = {
  geoCountryCode: '',
}

const PageContent = (props) => {
  const [state, setState] = useSetState(defaultState)

  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { active } = parsePermittedQueryParams(getQueryParamsFromHash(), ['active'])
  const isActive = active || 'true'

  const { geoSuburbsIndexUrl, inactiveGeoSuburbsIndexUrl } = generateUrls()

  const geoSuburbsPayload = useGeoSuburbs({
    filters: { active: isActive, ...pageFilters },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredGeoSuburbs,
    filteredGeoSuburbsCount,
    hasFilteredGeoSuburbs,
    loading,
  } = geoSuburbsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateOrEditSuburbModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const entities = useSelector(reduxState => reduxState.entities)

  useGeoStates({ performHttpRequests: true })

  return (
    <>
      <Helmet>
        <title>Suburbs | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateOrEditSuburbModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={isActive}
        tabBarItems={[
          {
            href: geoSuburbsIndexUrl,
            icon: faCheckCircle,
            key: 'true',
            title: 'Active',
          },
          {
            href: inactiveGeoSuburbsIndexUrl,
            icon: faBan,
            key: 'false',
            title: 'Inactive',
          },
        ]}
        title="Suburbs"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredGeoSuburbs && <ListBlankState />}

            {!loading && hasFilteredGeoSuburbs && (
              <DashboardModule title="Suburbs">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredGeoSuburbs.map(geoSuburb => (
                      <GeoSuburbListItem key={geoSuburb.id} geoSuburb={geoSuburb} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredGeoSuburbsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields(entities, state, setState)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
